import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import colors from "colors/color";
import { createPortal } from "react-dom";

// Enhanced styled components with better visual design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: theme.spacing(0.75),
    width: "100%",
    maxWidth: "calc(100% - 20px)",
    maxHeight: "calc(100vh - 20px)",
    boxShadow: "0 6px 20px rgba(0, 23, 231, 0.15)",
    background: "linear-gradient(to bottom, #ffffff, #f9f9ff)",
    margin: theme.spacing(0.5),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(1),
      margin: theme.spacing(0.75),
      maxWidth: "280px",
      maxHeight: "calc(100vh - 30px)",
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1.25),
      margin: theme.spacing(1),
      maxWidth: "320px",
      maxHeight: "calc(100vh - 40px)",
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.5),
      margin: theme.spacing(1.5),
      maxWidth: "360px",
      maxHeight: "calc(100vh - 50px)",
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(2),
      margin: theme.spacing(2),
      maxWidth: "400px",
      maxHeight: "calc(100vh - 60px)",
    },
  },
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(5px)",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  "& .MuiDialogContent-root": {
    overflow: "hidden",
    padding: theme.spacing(0.5),
    flex: 1,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up('xs')]: {
      padding: theme.spacing(0.75),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.25),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(1.5),
    },
  },
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(1),
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.12)",
  margin: "0 auto",
  width: "fit-content",
  maxWidth: "100%",
  transition: "all 0.3s ease",
  [theme.breakpoints.up('xs')]: {
    padding: theme.spacing(1.25),
    transform: "scale(0.95)",
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
    transform: "scale(1)",
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
    transform: "scale(1.05)",
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2.5),
    transform: "scale(1.1)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-3px",
    left: "-3px",
    right: "-3px",
    bottom: "-3px",
    background: "linear-gradient(45deg, #0017E7, #FFCCF8, #0017E7)",
    borderRadius: "23px",
    zIndex: -1,
    animation: "gradientAnimation 3s ease infinite",
    backgroundSize: "200% 200%",
  },
  "@keyframes gradientAnimation": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));

const LinkContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(0.75),
  backgroundColor: "#EAEAEA",
  borderRadius: "10px",
  marginTop: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.06)",
  border: "1px solid rgba(0, 0, 0, 0.04)",
  transition: "all 0.2s ease",
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
  [theme.breakpoints.up('xs')]: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    gap: theme.spacing(0.75),
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1.5),
    gap: theme.spacing(1),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    gap: theme.spacing(1.25),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    gap: theme.spacing(1.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: colors.primary,
  color: "white",
  fontFamily: "Poppins",
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 600,
  padding: "5px 12px",
  borderRadius: "10px",
  boxShadow: "0 3px 12px rgba(0, 23, 231, 0.35)",
  transition: "all 0.3s ease",
  transform: "translateY(-1px)",
  whiteSpace: "nowrap",
  "&:active": {
    transform: "translateY(1px)",
    boxShadow: "0 2px 6px rgba(0, 23, 231, 0.25)",
  },
  [theme.breakpoints.up('xs')]: {
    fontSize: "12px",
    padding: "6px 14px",
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: "13px",
    padding: "7px 16px",
  },
  [theme.breakpoints.up('md')]: {
    fontSize: "14px",
    padding: "8px 20px",
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: "15px",
    padding: "10px 24px",
  },
}));

const CopyIconButton = styled(IconButton)(({ theme }) => ({
  color: colors.primary,
  backgroundColor: "rgba(0, 23, 231, 0.15)",
  padding: "4px",
  transform: "scale(0.9)",
  transition: "all 0.2s ease",
  flexShrink: 0,
  [theme.breakpoints.up('xs')]: {
    padding: "4px",
    transform: "scale(0.9)",
  },
  [theme.breakpoints.up('sm')]: {
    padding: "5px",
    transform: "scale(1)",
  },
  [theme.breakpoints.up('md')]: {
    padding: "6px",
    transform: "scale(1.1)",
  },
  [theme.breakpoints.up('lg')]: {
    padding: "8px",
    transform: "scale(1.2)",
  },
}));

const CloseIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: 6,
  top: 6,
  color: "#555",
  backgroundColor: "rgba(0, 0, 0, 0.08)",
  padding: "4px",
  transition: "all 0.2s ease",
  zIndex: 1,
  [theme.breakpoints.up('xs')]: {
    right: 6,
    top: 6,
    padding: "4px",
  },
  [theme.breakpoints.up('sm')]: {
    right: 8,
    top: 8,
    padding: "5px",
  },
  [theme.breakpoints.up('md')]: {
    right: 10,
    top: 10,
    padding: "6px",
  },
  [theme.breakpoints.up('lg')]: {
    right: 12,
    top: 12,
    padding: "8px",
  },
}));

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
  position: 'fixed',
  zIndex: 99999,
  '& .MuiAlert-root': {
    position: 'relative',
    zIndex: 99999,
  }
}));

const SharePostLink = ({ open, onClose, shareUrl, title = "Share Post" }) => {
  const [showCopied, setShowCopied] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [qrCodeSize, setQRCodeSize] = useState(180);
  
  // Calculate sizes based on viewport height
  const calculateSizes = () => {
    const vh = window.innerHeight;
    let maxSize;
    
    if (vh < 500) { // Very small screens
      maxSize = Math.min(80, vh * 0.2);
    } else if (vh < 700) { // Small screens
      maxSize = Math.min(100, vh * 0.25);
    } else if (vh < 900) { // Medium screens
      maxSize = Math.min(120, vh * 0.3);
    } else { // Large screens
      maxSize = Math.min(140, vh * 0.35);
    }
    
    return maxSize;
  };

  useEffect(() => {
    const handleResize = () => {
      const containerWidth = document.querySelector('.MuiDialog-paper')?.clientWidth || window.innerWidth;
      const containerHeight = document.querySelector('.MuiDialog-paper')?.clientHeight || window.innerHeight;
      
      // Calculate QR size based on both width and height constraints
      const widthBasedSize = Math.min(
        containerWidth - 32,
        containerHeight * 0.35
      );
      
      const heightBasedSize = Math.min(
        containerHeight * 0.35,
        containerWidth - 32
      );
      
      const maxSize = Math.min(widthBasedSize, heightBasedSize);
      setQRCodeSize(Math.max(70, Math.min(maxSize, 160)));
    };
    
    if (open) {
      setTimeout(handleResize, 100);
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setShowCopied(true);
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };

  const handleCloseSnackbar = () => {
    setShowCopied(false);
  };

  // Create portal for Snackbar
  const renderSnackbar = () => {
    return createPortal(
      <StyledSnackbar
        open={showCopied}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            fontFamily: "Poppins",
            backgroundColor: colors.primary,
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
            "& .MuiAlert-icon": {
              marginRight: 1,
            },
            fontSize: "15px",
            fontWeight: 500,
            paddingY: 1.2,
            paddingX: 2,
            position: 'relative',
            zIndex: 99999,
          }}
        >
          Link copied successfully!
        </Alert>
      </StyledSnackbar>,
      document.body
    );
  };

  return (
    <>
      <StyledDialog 
        open={open} 
        onClose={onClose}
        disableScrollLock={true}
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins",
            textAlign: "center",
            fontWeight: 700,
            fontSize: { 
              xs: "13px",
              sm: "15px",
              md: "18px",
              lg: "22px"
            },
            color: colors.primary,
            pb: { 
              xs: 0.25,
              sm: 0.5,
              md: 0.75,
              lg: 1
            },
            pt: { 
              xs: 0.5,
              sm: 0.75,
              md: 1,
              lg: 1.5
            },
            background: "linear-gradient(90deg, #0017E7, #3344FF)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            width: "100%",
            px: { 
              xs: 0.5,
              sm: 0.75,
              md: 1,
              lg: 1.5
            },
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
          {title}
        </DialogTitle>
        <CloseIconButton onClick={onClose}>
          <CloseIcon />
        </CloseIconButton>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: { 
                xs: 0.75,
                sm: 1,
                md: 1.5,
                lg: 2
              },
              height: "100%",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <QRCodeContainer>
              <QRCodeCanvas
                value={shareUrl}
                size={qrCodeSize}
                level="H"
                includeMargin={true}
                bgColor={"#FFFFFF"}
                fgColor={colors.primary}
              />
            </QRCodeContainer>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: { 
                  xs: "9px",
                  sm: "10px",
                  md: "11px",
                  lg: "12px"
                },
                color: "#666",
                textAlign: "center",
                width: "100%",
                maxWidth: { 
                  xs: "180px",
                  sm: "200px",
                  md: "240px",
                  lg: "280px"
                },
                fontWeight: 500,
                lineHeight: 1.3,
                px: { 
                  xs: 0.5,
                  sm: 0.75,
                  md: 1,
                  lg: 1.25
                },
                boxSizing: "border-box",
              }}
            >
              Scan QR code or copy the link below to share this post
            </Typography>
            <LinkContainer>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: { 
                    xs: "8px",
                    sm: "9px",
                    md: "10px",
                    lg: "11px"
                  },
                  color: "#333",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                  minWidth: 0,
                }}
              >
                {shareUrl}
              </Typography>
              <CopyIconButton onClick={handleCopyLink}>
                <ContentCopyIcon fontSize={isMobile ? "small" : "medium"} />
              </CopyIconButton>
            </LinkContainer>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            pb: { 
              xs: 0.75,
              sm: 1,
              md: 1.5,
              lg: 2
            },
            pt: { 
              xs: 0.25,
              sm: 0.5,
              md: 0.75,
              lg: 1
            },
            px: { 
              xs: 0.5,
              sm: 0.75,
              md: 1,
              lg: 1.5
            },
            width: "100%",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
         
        </DialogActions>
      </StyledDialog>
      {renderSnackbar()}
    </>
  );
};

export default SharePostLink;